import { Component, ErrorInfo, PropsWithChildren } from 'react';
import { Alert } from '@amzn/awsui-components-react';
import { useMessages } from '../hooks/messages';
import { WEBSITE_ERRORS_BUNDLE } from '../utils/constants';
import { publishKatalMetric } from './katalAnalytics';

const ERROR_BOUNDARY_METRIC = 'errorBoundary_render';

/** Shown only if the language pack has not loaded when the boundary renders; mirrors the en bundle. */
const FALLBACK_TEXT = 'Something went wrong. Please refresh the page and try again.';

function ErrorBoundaryFallback() {
  const { message } = useMessages(WEBSITE_ERRORS_BUNDLE);
  return (
    <Alert type="error" data-testid="error-boundary-fallback">
      {message('error_boundary_fallback') || FALLBACK_TEXT}
    </Alert>
  );
}

interface ErrorBoundaryProps {
  /**
   * A value that changes on every navigation. When it changes while the fallback is showing, the
   * error is cleared so navigating (including a search-param change like a wizard step) recovers the
   * view instead of stranding the user on the fallback. It does not remount healthy children, so
   * in-progress form state survives normal navigation.
   */
  resetKey?: string;
}

/**
 * Catches render errors in its subtree and shows a localized message instead of a blank screen.
 * Mounted inside the localization provider so the fallback can resolve its text.
 */
export class ErrorBoundary extends Component<
  PropsWithChildren<ErrorBoundaryProps>,
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    publishKatalMetric(ERROR_BOUNDARY_METRIC, error.name || 'Error');
    console.error('ErrorBoundary caught a render error:', error, info.componentStack);
  }

  componentDidUpdate(prevProps: PropsWithChildren<ErrorBoundaryProps>): void {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  render() {
    return this.state.hasError ? <ErrorBoundaryFallback /> : this.props.children;
  }
}
